import React, { useMemo } from 'react';

import { ImageResizeOptions, Image } from 'wix-ui-tpa';

import { useTranslation } from '@wix/yoshi-flow-editor';

import {
  messagesModel,
  selectOneChatSettings,
  selectSettings,
} from '@/features';
import {
  senderIsAssistant,
  senderIsOwner,
  senderIsVisitor,
} from '@/utils/sender';
import { useAppSelector } from '@/store';
import { EXPERIMENTS } from '@/constants';

import {
  useRootProps,
  useWidgetSettings,
} from 'AiAssistantWidget/Widget/contexts';
import { iconsDict } from 'AiAssistantWidget/Settings/components';
import settingsParams from 'AiAssistantWidget/settingsParams';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { AIAvatarIcon, AvatarIcon } from '../svg';

import { classes, st } from './Avatar.st.css';

type AvatarProps = {
  message?: messagesModel.Message;
  memberIconClassname: string;
};

export const Avatar = ({ message, memberIconClassname }: AvatarProps) => {
  const { t } = useTranslation();
  const { experiments } = useAppExperiments();
  const { avatar: memberAvatar } = useRootProps();
  const widgetSettings = useWidgetSettings();

  const isOneChat = experiments.enabled(EXPERIMENTS.ONE_CHAT);
  const legacyAvatar = useAppSelector(selectSettings.avatar);
  const oneChatAiAvatar = useAppSelector(selectOneChatSettings.aiAvatar);
  const aiAvatar = isOneChat ? oneChatAiAvatar : legacyAvatar;
  const manualAvatar = useAppSelector(selectOneChatSettings.manualAvatar);

  const isOwnerMessage = senderIsOwner(message?.sender);
  const isAiMessage = senderIsAssistant(message?.sender);
  const isVisitorMessage = senderIsVisitor(message?.sender);

  const assistantAvatar = useMemo(() => {
    if (isOwnerMessage) {
      if (manualAvatar) {
        return (
          <Image
            width={28}
            height={28}
            aspectRatio="square"
            src={manualAvatar}
            resize={ImageResizeOptions.cover}
            role="img"
            // TODO: add proper aria-label for manual avatar
            // aria-label={aiAriaLabel}
          />
        );
      }
      // TODO: add proper aria-label for manual avatar
      return <AvatarIcon role="img" className={st(classes.icon)} />;
    }

    if (isAiMessage) {
      const aiAriaLabel = t('app.widget.ariallabel.aiAvatar');

      if (aiAvatar) {
        return (
          <Image
            width={28}
            height={28}
            aspectRatio="square"
            src={aiAvatar}
            resize={ImageResizeOptions.cover}
            role="img"
            aria-label={aiAriaLabel}
          />
        );
      }
      const iconKey = widgetSettings.get(
        settingsParams.minimizedButtonIcon,
        'primary',
      );
      return iconKey ? (
        <div role="img" aria-label={aiAriaLabel}>
          {iconsDict[iconKey]}
        </div>
      ) : (
        <AIAvatarIcon role="img" aria-label={aiAriaLabel} />
      );
    }

    return (
      // Fallback icon
      <AvatarIcon
        role="img"
        className={st(classes.icon)}
        // aria-label={t('app.widget.ariallabel.userAvatar')}
      />
    );
  }, [widgetSettings, aiAvatar, t, isAiMessage, isOwnerMessage, manualAvatar]);

  if (isVisitorMessage) {
    return memberAvatar ? (
      <img
        className={memberIconClassname}
        src={memberAvatar}
        alt={t('app.widget.chat.userPicAltText')}
      />
    ) : (
      <AvatarIcon
        role="img"
        aria-label={t('app.widget.ariallabel.userAvatar')}
        className={st(classes.icon)}
      />
    );
  }

  return assistantAvatar;
};
